import { dayjs } from '@karve.it/core';

import { deriveDocumentContext } from '@move-right/aegis/core/documentDerivation';
import type { AegisDocumentOption, AegisOptionValue } from '@move-right/aegis/core/documentOptions';

import { documentContextProblem } from '@move-right/aegis/core/documentValidation';
import type { AegisDocumentContext } from '@move-right/aegis/core/types/documentContext';
import { createSelector } from '@ngrx/store';

import { eventTypeInfoMapV2 } from '../../global.constants';

import { createDocumentDialogFeature } from './create-document-dialog.feature';
import { CreateDocumentDraft, CreateDocumentSubmission } from './create-document-dialog.state';

import { declaredValues, missingOptions, withoutOperationPrefix } from './create-document-dialog.util';
import { selectCreatableDocumentTypes, selectDocTypeNames } from './document-types';

const { selectDocuments, selectPreviewing } = createDocumentDialogFeature;

interface CreateDocumentStatus {
  context: AegisDocumentContext | undefined;
  missing: AegisDocumentOption[];
  problems: string[];
  ready: boolean;
  needsAttention: boolean;
}

const statuses = new WeakMap<CreateDocumentDraft, CreateDocumentStatus>();

const documentStatus = (draft: CreateDocumentDraft): CreateDocumentStatus => {
  const cached = statuses.get(draft);
  if (cached) return cached;
  const { preview, values, edited } = draft;
  const context = preview
    ? deriveDocumentContext(
      preview.context,
      preview.sources,
      { ...preview.context.variables, ...declaredValues(preview.options, values) },
    )
    : undefined;
  const missing = preview ? missingOptions(preview.options, values) : [];
  const contextProblem = preview && context ? documentContextProblem(preview.kind, context) : undefined;
  const backendProblems = preview && !edited && !missing.length
    ? preview.problems.map(withoutOperationPrefix)
    : [];
  const problems = [ ...new Set([ ...(contextProblem ? [ withoutOperationPrefix(contextProblem) ] : []), ...backendProblems ]) ];
  const status: CreateDocumentStatus = {
    context,
    missing,
    problems,
    ready: Boolean(preview) && !draft.previewLoading && !missing.length && !problems.length,
    needsAttention: Boolean(draft.previewError) || missing.length > 0 || problems.length > 0,
  };
  statuses.set(draft, status);
  return status;
};

export const selectOrderedDocuments = createSelector(
  selectCreatableDocumentTypes,
  selectDocuments,
  (slots, documents) => {
    const order = slots.map((slot) => slot.docType);
    const rank = (docType: string) => (order.includes(docType) ? order.indexOf(docType) : order.length);
    return [ ...documents ].sort((a, b) => rank(a.docType) - rank(b.docType));
  },
);

export const selectPreviewedDocument = createSelector(
  selectDocuments,
  selectPreviewing,
  (documents, previewing) => documents.find((doc) => doc.docType === previewing),
);

export const selectCustomerReachable = createSelector(
  selectDocuments,
  (documents) => documents.some((doc) => Boolean(doc.preview?.context.customer?.email || doc.preview?.context.customer?.phone)),
);

export const selectSubmission = createSelector(
  selectCustomerReachable,
  (reachable): CreateDocumentSubmission => (reachable ? 'send' : 'create'),
);

export interface EventChoiceView {
  id: string;
  title: string;
  typeName: string;
  color: string;
  date?: string;
  total: number;
  selected: boolean;
  disabled: boolean;
  note?: string;
}

export interface TransactionChoiceView {
  id: string;
  amount: number;
  currency: string;
  description: string;
  date?: string;
  selected: boolean;
  receiptSent: boolean;
}

export type OptionControlView =
  | { kind: 'events'; option: AegisDocumentOption; events: EventChoiceView[]; allSelected: boolean; selectedTotal: number }
  | { kind: 'transactions'; option: AegisDocumentOption; transactions: TransactionChoiceView[] }
  | { kind: 'invoice'; option: AegisDocumentOption; invoices: { label: string; value: string }[]; value?: string }
  | { kind: 'boolean' | 'text' | 'money' | 'date' | 'choice'; option: AegisDocumentOption; value?: AegisOptionValue };

const formatDate = (iso: string | undefined, timezone: string | undefined) =>
  (iso ? dayjs.tz(iso, timezone || undefined).format('ddd, MMM D, YYYY') : undefined);

const idList = (value: AegisOptionValue | undefined): string[] => (Array.isArray(value) ? value : []);

export const selectOptionControls = createSelector(
  selectPreviewedDocument,
  (doc): OptionControlView[] => {
    if (!doc?.preview) return [];
    const { preview, values } = doc;
    const { choices, context } = preview;
    const timezone = context.meta?.timezone;
    const currency = context.meta?.currency ?? 'USD';
    return preview.options.map((option): OptionControlView => {
      const value = values[option.name];
      switch (option.kind) {
        case 'events': {
          const chosen = idList(value);
          const events = choices.events
            .filter((event) => event.status !== 'cancelled')
            .map((event): EventChoiceView => {
              const info = eventTypeInfoMapV2[event.type];
              return {
                id: event.id,
                title: event.title || info?.name || event.type,
                typeName: info?.name ?? event.type,
                color: `var(--${info?.backgroundColor ?? 'primary-color'})`,
                date: formatDate(event.start, timezone),
                total: event.total,
                selected: event.billable && (!chosen.length || chosen.includes(event.id)),
                disabled: !event.billable,
                note: event.billable ? undefined : 'No charges',
              };
            });
          const selectable = events.filter((event) => !event.disabled);
          return {
            kind: 'events',
            option,
            events,
            allSelected: selectable.length > 0 && selectable.every((event) => event.selected),
            selectedTotal: events.filter((event) => event.selected).reduce((sum, event) => sum + event.total, 0),
          };
        }
        case 'transactions': {
          const chosen = idList(value);
          return {
            kind: 'transactions',
            option,
            transactions: choices.transactions.map((transaction): TransactionChoiceView => ({
              id: transaction.id,
              amount: transaction.amount,
              currency: transaction.currency ?? currency,
              description: [ transaction.paymentMethod, transaction.stage ].filter(Boolean).join(' · '),
              date: formatDate(transaction.paidAt ?? transaction.createdAt, timezone),
              selected: chosen.includes(transaction.id),
              receiptSent: Boolean(transaction.receiptArtifactId),
            })),
          };
        }
        case 'invoice':
          return {
            kind: 'invoice',
            option,
            invoices: choices.invoices.map((invoice) => ({
              label: invoice.number === undefined ? invoice.id : `Invoice #${invoice.number}`,
              value: invoice.id,
            })),
            value: typeof value === 'string' ? value : undefined,
          };
        default:
          return { kind: option.kind, option, value };
      }
    });
  },
);

export const selectCreateDocumentDialogViewModel = createSelector({
  state: createDocumentDialogFeature.selectCreateDocumentDialogState,
  docTypeNames: selectDocTypeNames,
  checklist: createSelector(
    selectCreatableDocumentTypes,
    selectOrderedDocuments,
    selectPreviewing,
    (slots, documents, previewing) => {
      const rows = slots.map((slot) => ({ docType: slot.docType, name: slot.name }));
      for (const doc of documents) {
        if (!rows.some((row) => row.docType === doc.docType)) rows.push({ docType: doc.docType, name: doc.docType });
      }
      return rows.map((row) => {
        const doc = documents.find((d) => d.docType === row.docType);
        return {
          ...row,
          ticked: Boolean(doc),
          previewing: row.docType === previewing,
          needsAttention: Boolean(doc) && documentStatus(doc).needsAttention,
        };
      });
    },
  ),
  documents: createSelector(
    selectOrderedDocuments,
    selectDocTypeNames,
    (documents, names) => documents.map((doc) => ({ ...doc, name: names[doc.docType] ?? doc.docType })),
  ),
  previewed: createSelector(
    selectPreviewedDocument,
    selectOrderedDocuments,
    selectDocTypeNames,
    (doc, documents, names) => {
      if (!doc) return undefined;
      const index = documents.indexOf(doc);
      const neighbour = (other: CreateDocumentDraft | undefined) =>
        (other ? { docType: other.docType, name: names[other.docType] ?? other.docType } : undefined);
      return {
        ...doc,
        ...documentStatus(doc),
        name: names[doc.docType] ?? doc.docType,
        position: index + 1,
        previous: neighbour(documents[index - 1]),
        next: neighbour(documents[index + 1]),
      };
    },
  ),
  needsAttention: createSelector(
    selectOrderedDocuments,
    selectDocTypeNames,
    (documents, names) => documents
      .filter((doc) => documentStatus(doc).needsAttention)
      .map((doc) => ({ docType: doc.docType, name: names[doc.docType] ?? doc.docType })),
  ),
  submitBlockedReason: createSelector(
    selectOrderedDocuments,
    selectDocTypeNames,
    (documents, names): string | undefined => {
      if (!documents.length) return 'Select a document';
      if (documents.some((doc) => doc.previewLoading)) {
        return documents.length > 1 ? 'Waiting for the previews to load' : 'Waiting for the preview to load';
      }
      const blocked = documents.filter((doc) => !documentStatus(doc).ready);
      if (!blocked.length) return undefined;
      const blockedNames = blocked.map((doc) => names[doc.docType] ?? doc.docType);
      if (blocked.length > 1) {
        return `${blockedNames.slice(0, -1).join(', ')} and ${blockedNames[blockedNames.length - 1]} need attention`;
      }
      const [ doc ] = blocked;
      const { missing, problems } = documentStatus(doc);
      const reason = doc.previewError
        ? 'The preview could not be loaded'
        : missing.length ? `${missing[0].label} is required` : problems[0] ?? 'The preview is not ready';
      return documents.length > 1 ? `${blockedNames[0]}: ${reason}` : reason;
    },
  ),
  documentsLabel: createSelector(
    selectOrderedDocuments,
    selectDocTypeNames,
    (documents, names) => (documents.length > 1
      ? `${documents.length} documents`
      : names[documents[0]?.docType] ?? documents[0]?.docType ?? 'Document'),
  ),
  retrying: createSelector(selectDocuments, (documents) => documents.some((doc) => doc.resending || doc.creating)),
  submitErrors: createSelector(
    createDocumentDialogFeature.selectSubmitErrors,
    selectDocTypeNames,
    (errors, names) => errors.map(({ docType, message }) =>
      (errors.length > 1 ? `${names[docType] ?? docType}: ${message}` : message)),
  ),
  allSent: createSelector(selectDocuments, (documents) => documents.every((doc) => doc.result?.sent)),
  resultHasProblems: createSelector(
    selectDocuments,
    (documents) => documents.some((doc) => doc.result?.createError || doc.result?.sendError),
  ),
  customerName: createSelector(
    selectDocuments,
    (documents) => documents.find((doc) => doc.preview)?.preview.context.customer?.displayName,
  ),
  jobCode: createSelector(
    selectDocuments,
    (documents) => documents.find((doc) => doc.preview)?.preview.context.job.code,
  ),
  controls: selectOptionControls,
  customerReachable: selectCustomerReachable,
  submission: selectSubmission,
});
